// Elementos de marca: carpetas de logos, elementos gráficos y tipografías —
// ADR-2026-10-19-elementos-de-marca.
//
// Los archivos son `brand_assets` (bucket privado `brand-assets`), los mismos que lee el
// Estudio de Video; las carpetas son `brand_asset_carpetas`, una lista por marca. La
// subida y el borrado de un archivo pasan por `utils/brandAssets.ts`, que es lo que ya
// usa el Estudio: así un archivo sale igual de donde se suba.
//
// Las carpetas tienen la MISMA forma que las de Estilos (`Moodboard`): se dibujan con la
// misma grilla (`GrillaDeCarpetas`) y comparten el catálogo de colores.

import { supabase } from '../../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from '../../utils/imageCache';
import {
  BRAND_ASSETS_BUCKET,
  CARPETA_POR_TIPO,
  deleteBrandAsset,
  uploadBrandAsset,
  type BrandAsset,
  type BrandAssetTipo,
} from '../../utils/brandAssets';
import type { Moodboard } from './moodboardsApi';

const TABLA_CARPETAS = 'brand_asset_carpetas';

/** El detalle técnico va a la consola; la persona ve qué no se pudo hacer, sin el error crudo
 *  de la base ("relation … does not exist"). */
function errorParaMostrar(queNoSePudo: string, causa: unknown): Error {
  console.error(`[elementosDeMarca] ${queNoSePudo}`, causa);
  return new Error(`${queNoSePudo}. Probá de nuevo.`);
}
const SIGNED_TTL = 3600;
/** Las miniaturas de la grilla. `contain`: un logo no se recorta. */
const GRILLA_PX = 480;
const PORTADA_PX = 240;
export const ELEMENTOS_EN_PORTADA = 3;
const CONCURRENCIA_FIRMAS = 8;

/** Los tipos que viven en Elementos de marca. `upload` (Edición gráfica) no. */
export const TIPOS_DE_ELEMENTO: readonly BrandAssetTipo[] = ['logo', 'element', 'font'];

/** Una carpeta: la forma de las de Estilos, para dibujarla con la misma grilla. */
export type CarpetaDeElementos = Moodboard;

export interface ElementoDeMarca {
  id: string;
  /** Lo guardado en `brand_assets.name`: el nombre del archivo, o el que le puso la persona. */
  nombre: string;
  /** Cómo se lo muestra y se lo nombra al agente: el nombre sin la extensión del archivo
   *  (ADR-2026-10-20-los-elementos-de-marca-tienen-titulo). */
  titulo: string;
  tipo: BrandAssetTipo;
  path: string;
  mimeType: string | null;
  createdAt: string;
  /** Miniatura firmada (imágenes) o el archivo firmado (tipografías, para dibujarlas). */
  url: string;
}

/** Extensiones de tipografía que acepta `uploadBrandAsset`. */
const EXTENSIONES_DE_FUENTE = /\.(woff2?|ttf|otf)$/i;

export const esArchivoDeFuente = (file: Pick<File, 'name' | 'type'>): boolean =>
  EXTENSIONES_DE_FUENTE.test(file.name) || /^font\//.test(file.type);

/**
 * Con qué `tipo` se guarda un archivo que se sube a una carpeta: tipografía si es un
 * archivo de fuente; si no, logo cuando la carpeta habla de logos y elemento gráfico en
 * el resto. El tipo es lo que mira el Estudio (sus logos, sus elementos, sus fuentes).
 */
export function tipoParaSubir(file: Pick<File, 'name' | 'type'>, nombreDeCarpeta: string): BrandAssetTipo {
  if (esArchivoDeFuente(file)) return 'font';
  return /logo/i.test(nombreDeCarpeta) ? 'logo' : 'element';
}

/** Las imágenes que la transformación de Supabase sabe achicar. El resto (SVG, GIF) se
 *  firma sin transformar: pedirle una miniatura a un SVG devuelve error. */
const SE_TRANSFORMA = /^image\/(jpeg|png|webp)$/;

async function firmar(path: string, px: number | null): Promise<string> {
  const clave = `brand-assets:${px ?? 'full'}:${path}`;
  const cacheada = getCachedSignedUrl(clave);
  if (cacheada) return cacheada;
  const { data } = await supabase.storage
    .from(BRAND_ASSETS_BUCKET)
    .createSignedUrl(
      path,
      SIGNED_TTL,
      px ? { transform: { width: px, height: px, resize: 'contain', quality: 80 } } : undefined,
    );
  const url = data?.signedUrl ?? '';
  if (url) setCachedSignedUrl(clave, url);
  return url;
}

/** Firma de a {@link CONCURRENCIA_FIRMAS}: una miniatura si es una imagen que se
 *  transforma, el archivo entero si no (SVG, GIF, tipografías). */
async function firmarVarios(filas: Pick<BrandAsset, 'storage_path' | 'mime_type'>[], px: number): Promise<string[]> {
  const out: string[] = [];
  for (let i = 0; i < filas.length; i += CONCURRENCIA_FIRMAS) {
    const tanda = filas.slice(i, i + CONCURRENCIA_FIRMAS);
    out.push(
      ...(await Promise.all(
        tanda.map((f) => firmar(f.storage_path, SE_TRANSFORMA.test(f.mime_type ?? '') ? px : null)),
      )),
    );
  }
  return out;
}

/** El archivo entero firmado: lo que viaja como referencia al agente y a la generación. */
export const firmarOriginal = (path: string): Promise<string> => firmar(path, null);

interface FilaDeCarpeta {
  id: string;
  nombre: string;
  brand_id: string;
  color: string | null;
  created_at: string;
}

/**
 * Las carpetas de una marca, de la más nueva a la más vieja, con cuántos archivos tienen y
 * la portada: las últimas imágenes subidas (las tipografías no se dibujan en el ícono).
 */
export async function fetchCarpetasDeElementos({
  companyId,
  brandId,
}: {
  companyId: string;
  brandId: string;
}): Promise<CarpetaDeElementos[]> {
  const { data: carpetas, error } = await supabase
    .from(TABLA_CARPETAS)
    .select('id, nombre, brand_id, color, created_at')
    .eq('id_company', companyId)
    .eq('brand_id', brandId)
    .order('created_at', { ascending: false });
  if (error) throw errorParaMostrar('No pudimos cargar las carpetas', error);
  const filas = (carpetas ?? []) as FilaDeCarpeta[];
  if (filas.length === 0) return [];

  const { data: archivos, error: errArchivos } = await supabase
    .from('brand_assets')
    .select('carpeta_id, storage_path, mime_type, tipo, created_at')
    .in('carpeta_id', filas.map((c) => c.id))
    .order('created_at', { ascending: false });
  if (errArchivos) throw errorParaMostrar('No pudimos cargar los elementos', errArchivos);

  const porCarpeta = new Map<string, Pick<BrandAsset, 'storage_path' | 'mime_type' | 'tipo'>[]>();
  for (const a of (archivos ?? []) as (Pick<BrandAsset, 'storage_path' | 'mime_type' | 'tipo'> & { carpeta_id: string })[]) {
    const lista = porCarpeta.get(a.carpeta_id) ?? [];
    lista.push(a);
    porCarpeta.set(a.carpeta_id, lista);
  }

  return Promise.all(
    filas.map(async (c) => {
      const deEsta = porCarpeta.get(c.id) ?? [];
      const imagenes = deEsta.filter((a) => a.tipo !== 'font').slice(0, ELEMENTOS_EN_PORTADA);
      const portada = (await firmarVarios(imagenes, PORTADA_PX)).filter(Boolean);
      return {
        id: c.id,
        nombre: c.nombre,
        brandId: c.brand_id,
        color: c.color,
        cantidadFotos: deEsta.length,
        portada,
        createdAt: c.created_at,
      };
    }),
  );
}

/** Los archivos de una carpeta, del más nuevo al más viejo, firmados para dibujarlos. */
export async function fetchElementos({ carpetaId }: { carpetaId: string }): Promise<ElementoDeMarca[]> {
  const { data, error } = await supabase
    .from('brand_assets')
    .select('*')
    .eq('carpeta_id', carpetaId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw errorParaMostrar('No pudimos cargar los elementos', error);
  const filas = (data ?? []) as BrandAsset[];
  const urls = await firmarVarios(filas, GRILLA_PX);
  return filas.map((f, i) => aElemento(f, urls[i]));
}

/** "logo-blanco.png" → "logo-blanco". Sin nombre, el de su tipo. */
export function tituloDeElemento(nombre: string | null | undefined, tipo: BrandAssetTipo): string {
  const limpio = (nombre ?? '').trim().replace(/\.[a-z0-9]{1,6}$/i, '').trim();
  if (limpio) return limpio;
  return tipo === 'font' ? 'Tipografía' : tipo === 'logo' ? 'Logo' : 'Elemento';
}

const aElemento = (f: BrandAsset, url: string): ElementoDeMarca => ({
  id: f.id,
  nombre: f.name ?? 'Sin nombre',
  titulo: tituloDeElemento(f.name, f.tipo),
  tipo: f.tipo,
  path: f.storage_path,
  mimeType: f.mime_type,
  createdAt: f.created_at,
  url,
});

export async function crearCarpetaDeElementos({
  companyId,
  brandId,
  nombre,
  color,
  userId,
}: {
  companyId: string;
  brandId: string;
  nombre: string;
  color?: string | null;
  userId?: string | null;
}): Promise<CarpetaDeElementos> {
  const limpio = nombre.trim() || 'Carpeta sin nombre';
  const { data, error } = await supabase
    .from(TABLA_CARPETAS)
    .insert({ id_company: companyId, brand_id: brandId, nombre: limpio, color: color ?? null, created_by: userId ?? null })
    .select('id, nombre, brand_id, color, created_at')
    .single();
  if (error || !data) throw errorParaMostrar('No pudimos crear la carpeta', error);
  const c = data as FilaDeCarpeta;
  return { id: c.id, nombre: c.nombre, brandId: c.brand_id, color: c.color, cantidadFotos: 0, portada: [], createdAt: c.created_at };
}

export async function renombrarCarpetaDeElementos(id: string, nombre: string): Promise<void> {
  const { error } = await supabase
    .from(TABLA_CARPETAS)
    .update({ nombre: nombre.trim() || 'Carpeta sin nombre', updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw errorParaMostrar('No pudimos guardar el nombre', error);
}

export async function pintarCarpetaDeElementos(id: string, color: string): Promise<void> {
  const { error } = await supabase
    .from(TABLA_CARPETAS)
    .update({ color, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw errorParaMostrar('No pudimos guardar el color', error);
}

/**
 * Borra la carpeta con lo que tiene adentro: primero los objetos del bucket, después la
 * fila (que se lleva las de `brand_assets` por cascade). Al revés, un fallo en el medio
 * dejaría archivos en el bucket que ninguna fila nombra.
 */
export async function eliminarCarpetaDeElementos({ carpetaId }: { carpetaId: string }): Promise<void> {
  const { data, error } = await supabase.from('brand_assets').select('storage_path, tipo').eq('carpeta_id', carpetaId);
  if (error) throw errorParaMostrar('No pudimos borrar la carpeta', error);
  // Con cada tipografía se va su hoja de muestra (si nunca se dibujó, borrarla no falla).
  const paths = ((data ?? []) as { storage_path: string; tipo: BrandAssetTipo }[]).flatMap((f) =>
    f.tipo === 'font' ? [f.storage_path, pathDeMuestra(f.storage_path)] : [f.storage_path],
  );
  if (paths.length > 0) {
    const { error: errStorage } = await supabase.storage.from(BRAND_ASSETS_BUCKET).remove(paths);
    if (errStorage) throw errorParaMostrar('No pudimos borrar los archivos', errStorage);
  }
  const { error: errFila } = await supabase.from(TABLA_CARPETAS).delete().eq('id', carpetaId);
  if (errFila) throw errorParaMostrar('No pudimos borrar la carpeta', errFila);
}

/** Sube un archivo a una carpeta con el tipo que le corresponde ({@link tipoParaSubir}). */
export async function subirElemento({
  companyId,
  brandId,
  carpeta,
  file,
}: {
  companyId: string;
  brandId: string;
  carpeta: Pick<CarpetaDeElementos, 'id' | 'nombre'>;
  file: File;
}): Promise<ElementoDeMarca> {
  const fila = await uploadBrandAsset({
    file,
    brandProfileId: brandId,
    companyId,
    tipo: tipoParaSubir(file, carpeta.nombre),
    carpetaId: carpeta.id,
  });
  const [url] = await firmarVarios([fila], GRILLA_PX);
  return aElemento(fila, url);
}

/** Le cambia el título a un elemento (`brand_assets.name`). Vacío no se guarda. */
export async function renombrarElemento(id: string, titulo: string): Promise<void> {
  const limpio = titulo.trim();
  if (!limpio) return;
  const { error } = await supabase.from('brand_assets').update({ name: limpio }).eq('id', id);
  if (error) throw errorParaMostrar('No pudimos guardar el nombre', error);
}

export async function eliminarElemento(elemento: Pick<ElementoDeMarca, 'id' | 'path' | 'tipo'>): Promise<void> {
  await deleteBrandAsset({ id: elemento.id, storage_path: elemento.path });
  // La hoja de muestra de una tipografía es un archivo aparte, al lado del original
  // (ADR-2026-10-21-revision-greptile-196-la-muestra-se-va-con-la-tipografia).
  if (elemento.tipo === 'font') {
    await supabase.storage.from(BRAND_ASSETS_BUCKET).remove([pathDeMuestra(elemento.path)]).catch(() => {});
  }
}

/**
 * Dónde queda la hoja de muestra de una tipografía: al lado del archivo, con `-muestra.png`
 * (ADR-2026-10-19-la-tipografia-viaja-como-hoja-de-muestra). Mismo prefijo de empresa, así la
 * policy del bucket la cubre.
 */
export const pathDeMuestra = (pathDeLaFuente: string): string =>
  `${pathDeLaFuente.replace(/\.[a-z0-9]{1,6}$/i, '')}-muestra.png`;

/**
 * La hoja de muestra de una tipografía, subida y firmada (el ORIGINAL, como todo lo que viaja
 * como referencia). Se dibuja en el navegador y se sube con `upsert`: elegir la misma
 * tipografía otra vez la reemplaza en vez de sumar copias.
 */
export async function subirMuestraDeTipografia(elemento: Pick<ElementoDeMarca, 'id' | 'nombre' | 'path' | 'url'>): Promise<string> {
  const { dibujarMuestraDeTipografia, canvasAPng } = await import('../../utils/muestraDeTipografia');
  const canvas = await dibujarMuestraDeTipografia({
    url: elemento.url,
    rotulo: `Tipografía · ${tituloDeElemento(elemento.nombre, 'font')}`,
    idUnico: elemento.id,
  });
  const png = await canvasAPng(canvas);
  const path = pathDeMuestra(elemento.path);
  const { error } = await supabase.storage
    .from(BRAND_ASSETS_BUCKET)
    .upload(path, png, { contentType: 'image/png', upsert: true });
  if (error) throw errorParaMostrar('No pudimos preparar la tipografía', error);
  const url = await firmarOriginal(path);
  if (!url) throw errorParaMostrar('No pudimos preparar la tipografía', 'sin firma');
  return url;
}

/** Una carpeta con lo justo para el agente: nombre y cuántos elementos tiene. */
export interface CarpetaDeElementosResumen {
  id: string;
  nombre: string;
  cantidad: number;
}

/**
 * Las carpetas de la marca para el agente (ADR-2026-10-20-el-agente-usa-los-elementos-de-marca):
 * sin firmar portadas, que el agente no mira. De la más nueva a la más vieja, como la grilla.
 */
export async function listarCarpetasDeElementos({
  companyId,
  brandId,
}: {
  companyId: string;
  brandId: string;
}): Promise<CarpetaDeElementosResumen[]> {
  const { data: carpetas, error } = await supabase
    .from(TABLA_CARPETAS)
    .select('id, nombre')
    .eq('id_company', companyId)
    .eq('brand_id', brandId)
    .order('created_at', { ascending: false });
  if (error) throw errorParaMostrar('No pudimos cargar las carpetas', error);
  const filas = (carpetas ?? []) as { id: string; nombre: string }[];
  if (filas.length === 0) return [];
  const { data: archivos, error: errArchivos } = await supabase
    .from('brand_assets')
    .select('carpeta_id')
    .in('carpeta_id', filas.map((c) => c.id));
  if (errArchivos) throw errorParaMostrar('No pudimos cargar los elementos', errArchivos);
  const cuenta = new Map<string, number>();
  for (const a of (archivos ?? []) as { carpeta_id: string }[]) cuenta.set(a.carpeta_id, (cuenta.get(a.carpeta_id) ?? 0) + 1);
  return filas.map((c) => ({ id: c.id, nombre: c.nombre, cantidad: cuenta.get(c.id) ?? 0 }));
}

/** Pasa un elemento a otra carpeta de la misma marca. */
export async function moverElemento(id: string, carpetaId: string): Promise<void> {
  const { error } = await supabase.from('brand_assets').update({ carpeta_id: carpetaId }).eq('id', id);
  if (error) throw errorParaMostrar('No pudimos mover el elemento', error);
}

/**
 * Sube a una carpeta una imagen que ya está en otro lado (una generada, el logo, un producto):
 * la baja de su URL firmada y la sube como cualquier archivo. Lo usa el agente
 * (`guardar_en_elementos_de_marca`), como `subirFotoDesdeUrl` en Estilos.
 */
export async function subirElementoDesdeUrl({
  companyId,
  brandId,
  carpeta,
  url,
  titulo,
}: {
  companyId: string;
  brandId: string;
  carpeta: Pick<CarpetaDeElementos, 'id' | 'nombre'>;
  url: string;
  titulo?: string | null;
}): Promise<ElementoDeMarca> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No pude bajar la imagen (${res.status}).`);
  const blob = await res.blob();
  if (blob.size > 25 * 1024 * 1024) throw new Error('La imagen pesa más de 25 MB.');
  const tipoMime = blob.type || 'image/png';
  const ext = tipoMime.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
  const nombre = `${(titulo ?? '').trim() || 'Elemento'}.${ext}`;
  return subirElemento({ companyId, brandId, carpeta, file: new File([blob], nombre, { type: tipoMime }) });
}

/**
 * Guarda una imagen que ya existe (por URL) como ELEMENTO GRÁFICO de la marca, en la carpeta
 * de su tipo ("Elementos gráficos", que se crea si no está). Lo usa "Guardar como elemento"
 * de una capa del visor (ADR-2026-10-20-una-capa-se-guarda-como-elemento-de-marca).
 * Devuelve el nombre de la carpeta, para contarle a la persona dónde quedó.
 */
export async function guardarImagenComoElemento({
  companyId,
  brandId,
  url,
  titulo,
}: {
  companyId: string;
  brandId: string;
  url: string;
  titulo: string;
}): Promise<{ carpeta: string }> {
  const res = await fetch(url);
  if (!res.ok) throw new Error('No pudimos bajar la imagen. Probá de nuevo.');
  const blob = await res.blob();
  const tipoMime = blob.type || 'image/png';
  const ext = tipoMime.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
  const nombre = `${titulo.trim() || 'Elemento'}.${ext}`;
  await uploadBrandAsset({
    file: new File([blob], nombre, { type: tipoMime }),
    brandProfileId: brandId,
    companyId,
    tipo: 'element',
  });
  return { carpeta: CARPETA_POR_TIPO.element?.nombre ?? 'Elementos gráficos' };
}
